import { useEffect, useMemo, useState } from 'react';

import { useOwnTaskIds } from '../ownTaskIds';

/**
 * How long a released arrival wears the wash — the board's own figure for a
 * card that moved here (spec 078, `useBoardMotion`), so the two cues fade
 * together.
 */
const ARRIVED_MS = 6000;

interface HoldRecord {
  scopeKey: string;
  /** Ids the page has shown; `null` until the first non-empty list. */
  shown: ReadonlySet<string> | null;
}

const EMPTY: ReadonlySet<string> = new Set();
const NEVER_HELD = () => false;

function seed<T>(
  items: readonly T[],
  scopeKey: string,
  idOf: (item: T) => string,
): HoldRecord {
  return {
    scopeKey,
    shown: items.length > 0 ? new Set(items.map(idOf)) : null,
  };
}

/**
 * New items arrive in place, unless the person is in the middle of something
 * (spec 148, superseding spec 126's gate).
 *
 * The page renders `visible`: the items it has already shown, the ones the
 * person caused themselves (`useOwnTaskIds`), and anything a refresh brought
 * that `isHolding` does not hold back. A held item waits until `isHolding`
 * says otherwise, so every item a hold kept is let in by the same render —
 * they drop in together. Once shown an item stays shown; a hold never takes a
 * card back off the page.
 *
 * `arrived` names what came in from elsewhere, for the arrival wash (spec 078
 * FR-008), and empties a few seconds after the latest of them. The person's
 * own tasks are not in it: nothing they just made is news to them.
 *
 * The record is seeded by the first non-empty list, and again whenever
 * `scopeKey` changes — a different filter or workflow is a different view, not
 * an arrival. An item that leaves while held simply never shows, and one that
 * moves while held is judged, and shown, where it is when the hold ends.
 */
export function useArrivalHold<T>(
  items: readonly T[],
  scopeKey: string,
  /** How an item names its task; tasks carry `id`, list rows `taskId`. */
  idOf: (item: T) => string = (item) => (item as { id: string }).id,
  /** Whether an unseen item must wait. Keep it stable (`useCallback`). */
  isHolding: (item: T) => boolean = NEVER_HELD,
): { visible: T[]; arrived: ReadonlySet<string> } {
  const own = useOwnTaskIds((state) => state.ids);
  const [record, setRecord] = useState<HoldRecord>(() =>
    seed(items, scopeKey, idOf),
  );
  const [arrived, setArrived] = useState<ReadonlySet<string>>(EMPTY);

  // Adjusted during render, the documented shape for state derived from a
  // prop: a new scope, or the first list with anything in it, seeds the record.
  let current = record;
  if (
    record.scopeKey !== scopeKey ||
    (record.shown === null && items.length > 0)
  ) {
    current = seed(items, scopeKey, idOf);
    setRecord(current);
  }

  const { visible, adopted, released } = useMemo(() => {
    const shown = current.shown;
    if (shown === null)
      return { visible: [...items], adopted: [], released: [] };
    const visibleItems: T[] = [];
    // Shown for the first time: the person's own, and the arrivals let in.
    const adoptedIds: string[] = [];
    const releasedIds: string[] = [];
    for (const item of items) {
      const id = idOf(item);
      if (shown.has(id)) {
        visibleItems.push(item);
      } else if (own.has(id)) {
        visibleItems.push(item);
        adoptedIds.push(id);
      } else if (!isHolding(item)) {
        visibleItems.push(item);
        adoptedIds.push(id);
        releasedIds.push(id);
      }
    }
    return {
      visible: visibleItems,
      adopted: adoptedIds,
      released: releasedIds,
    };
  }, [current.shown, idOf, isHolding, items, own]);

  // Recorded in the render that shows them, so a hold that begins a moment
  // later cannot take them back off the page.
  if (adopted.length > 0 && current.shown !== null) {
    setRecord({
      scopeKey: current.scopeKey,
      shown: new Set([...current.shown, ...adopted]),
    });
  }
  if (released.length > 0) {
    setArrived((prev) => new Set([...prev, ...released]));
  }

  useEffect(() => {
    if (arrived.size === 0) return;
    const timer = window.setTimeout(() => {
      setArrived(EMPTY);
    }, ARRIVED_MS);
    return () => {
      window.clearTimeout(timer);
    };
  }, [arrived]);

  return { visible, arrived };
}
